import { useEffect, useRef, useState } from "react";
import { Button, Progress } from "antd";

import DashboardApi from "../../api/DashboardApi";
import DeviceApi from "../../api/DeviceApi";
import { DeviceMap } from "./device-map/DeviceMap";
import { DashboardHeader, DashboardWrapper, DeviceDetailsSideBar, DeviceName, Statistics, StatisticsContent, StatisticsDesc, StatisticsItem, StatisticsItemContent, StatisticsText, StatisticsTitle, StatisticsValue, DeviceInformation, DeviceInfoItem, DeviceInfoItemLabel, DeviceInfoItemValue, DeviceRealtimeMetrics, DeviceMapWrapper, CloseButtonWrapper } from "./Dashboard.styled";
import RealtimeDeviceMetrics from "./device-map/realtime-device-metrics/RealtimeDeviceMetrics";
import DeviceStatus from "../../components/ui-components/device-status/DeviceStatus";
import DeviceRssi from "../../components/ui-components/rssi/DeviceRssi";
import TimeUtils from "../../util/TimeUtils";
import ViewStateService from "../../services/view-state/ViewStateService";
import { CloseOutlined } from "@ant-design/icons";

interface DashboardProps {

}

const Dashboard: React.FC<DashboardProps> = (props)=> {

    ViewStateService.getInstance().setStatusBarTitle("系统总览");

    // api
    let dashboardApi = new DashboardApi();
    let deviceApi = new DeviceApi();

    // ref
    let mapRef = useRef<any>();
    let minMapZoom = useRef(3);
    
    // state
    let [selectedDeviceView, setSelectedDeviceView] = useState(null);
    let [openSideBar, setOpenSideBar] = useState(false);
    let [systemOverview, setSystemOverview] = useState(null);
    let [deviceViews, setDeviceViews] = useState([]);

    let onDeviceSelected = (deviceView: any)=> {
        setSelectedDeviceView(deviceView);
        setOpenSideBar(true);
    }

    let getPercent = (val, total)=> {
        if (total == 0) {
            return 0;
        }
        return (val / total) * 100;
    };

    // onload
    useEffect(()=> {
        let loadSystemOverview = async ()=> {
            let systemRealtimeOverview = (await dashboardApi.getSystemRealtimeOverview()).data.data;
            setSystemOverview(systemRealtimeOverview);
        };

        let loadDeviceViews = async ()=> {
            let deviceViews = (await deviceApi.getAllDeviceViews()).data.data;
            setDeviceViews(deviceViews);
        }

        loadSystemOverview();
        loadDeviceViews();

        let refreshTask = setInterval(()=> {
            loadSystemOverview();
            loadDeviceViews();
        }, 5000);

        return ()=> {
            clearInterval(refreshTask);
        }
    }, []);

    // render
    return (
        <DashboardWrapper>
            { systemOverview && <DashboardHeader>
                <StatisticsItem>
                    <StatisticsItemContent>
                        <StatisticsTitle>设备数</StatisticsTitle>
                        <StatisticsContent>
                            <Statistics>
                                <StatisticsText>
                                    <StatisticsValue>{systemOverview.deviceOverview.total} <span style={{fontSize: '12px'}}></span></StatisticsValue>
                                    <div>
                                        <StatisticsDesc>{systemOverview.deviceOverview.online} 在线</StatisticsDesc>
                                        <StatisticsDesc>{systemOverview.deviceOverview.offline} 离线</StatisticsDesc>
                                    </div>
                                </StatisticsText>
                                <Progress
                                    size="small" 
                                    percent={100} 
                                    strokeColor="rgb(249, 125, 90)" 
                                    success={{percent: getPercent(systemOverview.deviceOverview.online, systemOverview.deviceOverview.total), strokeColor: 'rgb(28, 178, 115)'}} 
                                    showInfo={false} 
                                    />
                            </Statistics>
                        </StatisticsContent>
                    </StatisticsItemContent>
                </StatisticsItem>

                <StatisticsItem>
                    <StatisticsItemContent>
                        <StatisticsTitle>告警事件</StatisticsTitle>
                        <StatisticsContent>
                            <Statistics>
                                <StatisticsText>
                                    <StatisticsValue>{systemOverview.alarmEventsOverview.unacked} <span style={{fontSize: '12px'}}></span></StatisticsValue>
                                    <div>
                                        <StatisticsDesc>{systemOverview.alarmEventsOverview.unacked} 未处理</StatisticsDesc>
                                        <StatisticsDesc>{systemOverview.alarmEventsOverview.acked} 已处理</StatisticsDesc>
                                    </div>
                                </StatisticsText>
                                <Progress 
                                    size="small" 
                                    percent={100} 
                                    strokeColor="rgb(28, 178, 115)" 
                                    success={{percent: getPercent(systemOverview.alarmEventsOverview.unacked, systemOverview.alarmEventsOverview.total), strokeColor: 'red'}} 
                                    showInfo={false} 
                                    />
                            </Statistics>
                        </StatisticsContent>
                    </StatisticsItemContent>
                </StatisticsItem>

                
                <StatisticsItem>
                    <StatisticsItemContent>
                        <StatisticsTitle>流量池</StatisticsTitle>
                        <StatisticsContent>
                            <Statistics>
                                <StatisticsText>
                                    <StatisticsValue>{systemOverview.networkTrafficPool.used.toFixed(2)} <span style={{fontSize: '12px'}}>MB</span></StatisticsValue>
                                    <div>
                                        <StatisticsDesc>{systemOverview.networkTrafficPool.total.toFixed(2)}MB 总量</StatisticsDesc>
                                    </div>
                                </StatisticsText>
                                <Progress 
                                    size="small" 
                                    percent={100} 
                                    strokeColor="grey" 
                                    success={{percent: getPercent(systemOverview.networkTrafficPool.used, systemOverview.networkTrafficPool.total), strokeColor: 'rgb(28, 178, 115)'}} 
                                    showInfo={false} 
                                    />
                            </Statistics>
                        </StatisticsContent>
                    </StatisticsItemContent>
                </StatisticsItem>
            </DashboardHeader>
            }

            <DeviceMapWrapper>
                <DeviceMap onDeviceSelected={onDeviceSelected} />

                { selectedDeviceView && 
                <DeviceDetailsSideBar style={{transform: openSideBar ? 'translateX(0%)' : 'translateX(100%)'}}>
                    <DeviceName>
                        {selectedDeviceView.device.name}
                        <Button type="link" onClick={()=> setOpenSideBar(false)}><CloseButtonWrapper><CloseOutlined /></CloseButtonWrapper></Button>
                    </DeviceName>
                    <DeviceInformation>
                        <DeviceInfoItem>
                            <DeviceInfoItemLabel>设备状态</DeviceInfoItemLabel>
                            <DeviceInfoItemValue><DeviceStatus status={selectedDeviceView.device.status} /></DeviceInfoItemValue>
                        </DeviceInfoItem>

                        <DeviceInfoItem>
                            <DeviceInfoItemLabel>信号强度</DeviceInfoItemLabel>
                            <DeviceInfoItemValue><DeviceRssi deviceRssi={selectedDeviceView.latestHeartBeatMetric.rssi} /></DeviceInfoItemValue>
                        </DeviceInfoItem>

                        <DeviceInfoItem>
                            <DeviceInfoItemLabel>告警状态</DeviceInfoItemLabel>
                            <DeviceInfoItemValue>正常</DeviceInfoItemValue>
                        </DeviceInfoItem>

                        <DeviceInfoItem>
                            <DeviceInfoItemLabel>定位方式</DeviceInfoItemLabel>
                            <DeviceInfoItemValue>{selectedDeviceView.latestHeartBeatMetric?.altitude == null ? '基站定位': 'GNSS'}</DeviceInfoItemValue>
                        </DeviceInfoItem>

                        <DeviceInfoItem>
                            <DeviceInfoItemLabel>最后在线时间</DeviceInfoItemLabel>
                            <DeviceInfoItemValue>{selectedDeviceView.device.status == 'ONLINE' ? 'N/A' : TimeUtils.formatLastTime(selectedDeviceView.latestHeartBeatMetric.timestamp)}</DeviceInfoItemValue>
                        </DeviceInfoItem>

                        <DeviceInfoItem>
                            <DeviceInfoItemLabel>序列号 SN</DeviceInfoItemLabel>
                            <DeviceInfoItemValue>{selectedDeviceView.node.sn}</DeviceInfoItemValue>
                        </DeviceInfoItem>

                        <DeviceInfoItem>
                            <DeviceInfoItemLabel>ICCID</DeviceInfoItemLabel>
                            <DeviceInfoItemValue>{selectedDeviceView.node.iccid}</DeviceInfoItemValue>
                        </DeviceInfoItem>

                        <DeviceInfoItem>
                            <DeviceInfoItemLabel>运营商</DeviceInfoItemLabel>
                            <DeviceInfoItemValue>{selectedDeviceView.node.oper}</DeviceInfoItemValue>
                        </DeviceInfoItem>

                        <DeviceInfoItem>
                            <DeviceInfoItemLabel>固件版本</DeviceInfoItemLabel>
                            <DeviceInfoItemValue>Ver {selectedDeviceView.node.firmwareVersion}</DeviceInfoItemValue>
                        </DeviceInfoItem>
                    </DeviceInformation>
                    
                    <DeviceRealtimeMetrics>
                        <RealtimeDeviceMetrics deviceView={selectedDeviceView} />
                    </DeviceRealtimeMetrics>
                </DeviceDetailsSideBar>
                }
            </DeviceMapWrapper>
        </DashboardWrapper>
    );
}

export default Dashboard;
